import * as React from "react";

type UseOnScreenOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  /**
   * If true, the observer continues to fire on each intersection.
   * If false (default), the observer disconnects after the callback fires once.
   */
  continuous?: boolean;
};

/**
 * Returns a ref which can be attached to a DOM element. When attached, the
 * provided callback will be called when the element becomes visible in the
 * viewport.
 *
 * The callback will also be called immediately upon the ref mounting, if the
 * element is already visible.
 */
export function useOnScreen(
  callback: () => void,
  options: UseOnScreenOptions = {},
) {
  const { continuous = false, ...observerOptions } = options;
  const ref = React.useRef<HTMLElement | null>(null);
  const observer = React.useRef<IntersectionObserver | null>(null);

  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }

      if (node) {
        observer.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                callback();
                if (!continuous && observer.current) {
                  observer.current.disconnect();
                }
              }
            });
          },
          {
            root: observerOptions.root ?? null,
            rootMargin: observerOptions.rootMargin ?? "0px",
            threshold: observerOptions.threshold ?? 0.1,
          },
        );
        observer.current.observe(node);
      }

      ref.current = node;
    },
    [
      callback,
      continuous,
      observerOptions.root,
      observerOptions.rootMargin,
      observerOptions.threshold,
    ],
  );

  React.useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return setRef;
}
